import React, { useState, useEffect, useCallback } from "react";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import CourseHeader from "../components/Courses/Shared/CourseHeader";
import ProgramBenefits from "../components/Courses/Shared/ProgramBenefits";
import AnimatedSection from "../components/Courses/Shared/AnimatedSection";
import ProfessionalCourseApplicationForm from "../components/Courses/ProfessionalCourses/ProfessionalCourseApplicationForm";
import cyberImg from "../assets/cybersecurity solutions.jpeg";
import cmsApi from "../services/cmsApi";
import { getImageUrl } from "../utils/getImageUrl";
import {
  Cloud, KeyRound, Users, Fingerprint, Mail, Globe,
  Shield, Building2, Smartphone, Database, Share2, ShieldCheck,
  RotateCcw, Lock, CreditCard, Eye, Wifi, Monitor,
} from "lucide-react";

// brand constants
const BRAND_GOLD = "#fca903";
const BRAND_PURPLE_DARK = "#3b0764";
const BRAND_PURPLE_MID  = "#5d0470";

function parseBenefits(json) {
  try { return JSON.parse(json); }
  catch { return []; }
}

// ─── Quick Navigation ─────────────────────────────────────────────────────────

const TOC_ITEMS = [
  { id: "certification-programs", label: "Certification Programs" },
  { id: "program-benefits",       label: "Program Benefits"       },
  { id: "admission-requirements", label: "Admission Requirements" },
  { id: "application-form",       label: "Apply Now"              },
];

const SECTION_IDS = TOC_ITEMS.map((t) => t.id);

function useActiveSection() {
  const [active, setActive] = useState(SECTION_IDS[0]);
  useEffect(() => {
    const update = () => {
      const navbar = document.querySelector("header.sticky");
      const offset = (navbar ? navbar.getBoundingClientRect().height : 100) + 24;
      let current = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= offset) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, []);
  return active;
}

function QuickNavigation({ activeSection }) {
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const navbar = document.querySelector("header.sticky");
    const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 100;
    const top = el.getBoundingClientRect().top + window.pageYOffset - navbarHeight - 16;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  return (
    <nav className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
      <div className="px-5 py-4" style={{ background: `linear-gradient(135deg, ${BRAND_PURPLE_DARK} 0%, ${BRAND_PURPLE_MID} 100%)` }}>
        <p className="text-white font-bold text-sm uppercase tracking-widest">Quick Navigation</p>
      </div>
      <ol className="p-4 space-y-1">
        {TOC_ITEMS.map((item, idx) => {
          const isActive = activeSection === item.id;
          return (
            <li key={item.id}>
              <button
                onClick={() => scrollTo(item.id)}
                className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-purple-50 font-semibold border-l-4 pl-2"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
                style={isActive ? { color: BRAND_PURPLE_DARK, borderColor: BRAND_GOLD } : {}}
              >
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold mt-0.5 ${
                    isActive ? "text-white" : "bg-gray-200 text-gray-500"
                  }`}
                  style={isActive ? { background: BRAND_GOLD } : {}}
                >
                  {idx + 1}
                </span>
                <span className="leading-snug">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* ── SVG check / cross icons ── */
function CheckIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r="9" fill={color} fillOpacity="0.15" />
      <path d="M5 9.5l3 3 5-5.5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r="9" fill="#94a3b8" fillOpacity="0.15" />
      <path d="M6 6l6 6M12 6l-6 6" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/* ── Renders "C|EH AI Elite" — replaces the pipe char with a tall white bar ── */
function BundleLogoText({ name }) {
  const parts = name.split("|");
  if (parts.length < 2) {
    return <span className="font-black text-white text-xl leading-tight">{name}</span>;
  }
  return (
    <span className="font-black text-white text-xl leading-tight" style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
      {parts[0]}
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: "2px",
          height: "1.5em",
          background: "rgba(255,255,255,0.9)",
          borderRadius: "2px",
          flexShrink: 0,
          verticalAlign: "middle",
        }}
      />
      {parts.slice(1).join("|")}
    </span>
  );
}

/* ── Single bundle card ── */
function BundleCard({ name, tagline, headerColor, features, includedFeatures, excludedFeatures, popular }) {
  const allFeatures = features
    ? features.split("\n").filter(Boolean)
    : includedFeatures
      ? [
          ...includedFeatures.split("\n").filter(Boolean).map((f) => ({ text: f, included: true })),
          ...(excludedFeatures || "").split("\n").filter(Boolean).map((f) => ({ text: f, included: false })),
        ]
      : [];

  const isBundle1Style = !includedFeatures; // bundle 1 has plain features array

  return (
    <div
      className="flex flex-col overflow-hidden shadow-xl"
      style={{ border: popular ? `2px solid ${headerColor}` : "2px solid #e2e8f0", background: "#fff", flex: 1, minWidth: 0 }}
    >
      {/* Header */}
      <div
        className="px-6 py-5 flex items-center justify-between gap-3"
        style={{ background: headerColor }}
      >
        <BundleLogoText name={name} headerColor={headerColor} />
        {popular && (
          <span
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap"
            style={{ background: BRAND_GOLD, color: BRAND_PURPLE_DARK }}
          >
            <CheckIcon color={BRAND_PURPLE_DARK} />
            Most Popular
          </span>
        )}
      </div>

      {/* Tagline */}
      <div className="px-6 pt-5 pb-3">
        <p className="text-sm font-bold tracking-wide uppercase" style={{ color: headerColor }}>
          {tagline}
        </p>
      </div>

      {/* Divider */}
      <div className="mx-6 h-px bg-slate-200" />

      {/* Features */}
      <ul className="flex flex-col gap-3 px-6 py-5 flex-1">
        {isBundle1Style
          ? allFeatures.map((feat, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <CheckIcon color={headerColor} />
                <span>{feat}</span>
              </li>
            ))
          : allFeatures.map((feat, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm"
                style={{ color: feat.included ? "#374151" : "#94a3b8" }}
              >
                {feat.included ? <CheckIcon color={headerColor} /> : <CrossIcon />}
                <span>{feat.text}</span>
              </li>
            ))}
      </ul>
    </div>
  );
}

/* ── Bundle comparison section rendered inside the dark purple panel ── */
function CEHBundleComparison({ heading, subtitle, bundle1, bundle2 }) {
  return (
    <div className="px-6 md:px-10 py-8 md:py-10">
      {/* Gold separator from CEH row above */}
      <div className="h-px mb-8" style={{ backgroundColor: `${BRAND_GOLD}40` }} />

      <h4
        className="text-xl md:text-2xl font-bold mb-2"
        style={{ color: "#fff" }}
      >
        {heading}
      </h4>
      <p className="text-sm mb-6" style={{ color: "rgba(233,213,255,0.75)" }}>
        {subtitle}
      </p>

      <div className="flex flex-col md:flex-row gap-5">
        <BundleCard
          name={bundle1.name}
          tagline={bundle1.tagline}
          headerColor={bundle1.color}
          features={bundle1.features}
          popular={bundle1.popular}
        />
        <BundleCard
          name={bundle2.name}
          tagline={bundle2.tagline}
          headerColor={bundle2.color}
          includedFeatures={bundle2.included}
          excludedFeatures={bundle2.excluded}
          popular={false}
        />
      </div>
    </div>
  );
}

/* ── CCT stat highlight card ── */
function CCTStatCard({ value, label, text }) {
  return (
    <div
      className="bg-white rounded-xl p-5 shadow-lg flex flex-col gap-2"
      style={{ borderTop: `4px solid ${BRAND_PURPLE_DARK}` }}
    >
      <span
        className="font-black leading-none"
        style={{ fontSize: "clamp(2.2rem,5vw,3rem)", color: BRAND_GOLD }}
      >
        {value}
      </span>
      <p className="font-bold text-sm leading-snug" style={{ color: BRAND_PURPLE_DARK }}>{label}</p>
      <p className="text-xs text-gray-500 leading-relaxed">{text}</p>
    </div>
  );
}

/* ── CCT single offering row ── */
function CCTOfferingItem({ title, description }) {
  return (
    <div
      className="flex gap-4 items-start py-4"
      style={{ borderBottom: `1px solid ${BRAND_GOLD}25` }}
    >
      <span className="font-black text-base flex-shrink-0 mt-0.5" style={{ color: BRAND_GOLD }}>
        &#187;&#187;
      </span>
      <div>
        <p className="font-bold text-sm mb-1" style={{ color: BRAND_GOLD }}>{title}</p>
        <p className="text-sm leading-relaxed" style={{ color: "rgba(233,213,255,0.85)" }}>{description}</p>
      </div>
    </div>
  );
}

/* ── CCT full rich content block — renders inside the dark purple panel below the CCT row ── */
function CCTRichContent({ data }) {
  return (
    <div className="px-6 md:px-10 py-8 md:py-10">
      <div className="h-px mb-8" style={{ backgroundColor: `${BRAND_GOLD}40` }} />

      {/* 1 — Enhanced intro */}
      <p className="text-base leading-relaxed mb-8" style={{ color: "rgba(233,213,255,0.9)" }}>
        {data.enhancedDesc}
      </p>

      {/* 2 — Talent context banner */}
      <div
        className="rounded-xl p-6 mb-10"
        style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${BRAND_GOLD}50` }}
      >
        <h5 className="font-black text-lg mb-3" style={{ color: BRAND_GOLD }}>
          {data.talentHeadline}
        </h5>
        <p className="text-sm leading-relaxed text-white">{data.talentBody}</p>
      </div>

      {/* 3 — Stat cards */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-5">What Makes C|CT Unique?</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {data.stats.map((s, i) => (
          <CCTStatCard key={i} value={s.value} label={s.label} text={s.text} />
        ))}
      </div>

      {/* 4 — Key offerings */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-2">What Does C|CT Offer?</h4>
      <div className="mb-10">
        {data.offerings.map((o, i) => (
          <CCTOfferingItem key={i} title={o.title} description={o.desc} />
        ))}
      </div>

      {/* 5 — Job role pills */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-2">Career Roles After C|CT</h4>
      <p className="text-sm mb-5" style={{ color: "rgba(233,213,255,0.75)" }}>
        C|CT-certified professionals are equipped to pursue the following in-demand roles:
      </p>
      <div className="flex flex-wrap gap-2 mb-10">
        {data.jobRoles.map((role, i) => (
          <span
            key={i}
            className="px-4 py-2 rounded-full text-sm font-bold text-white"
            style={{ background: "#6b21a8", border: `1px solid ${BRAND_GOLD}50` }}
          >
            {role}
          </span>
        ))}
      </div>

      {/* 6 — Who should enroll */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-2">Who Should Enroll?</h4>
      <p className="text-sm mb-6" style={{ color: "rgba(233,213,255,0.75)" }}>
        {data.enrollIntro}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { title: data.audience1Title, desc: data.audience1Desc, icon: "briefcase" },
          { title: data.audience2Title, desc: data.audience2Desc, icon: "graduation" },
        ].map((a, i) => (
          <div
            key={i}
            className="bg-white rounded-xl p-6 flex flex-col gap-3"
            style={{ border: `1px solid rgba(59,7,100,0.2)` }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `${BRAND_GOLD}20` }}
            >
              {a.icon === "briefcase" ? (
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={BRAND_GOLD} strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
                </svg>
              ) : (
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={BRAND_GOLD} strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5M6 12v5c3 3 9 3 12 0v-5" />
                </svg>
              )}
            </div>
            <p className="font-bold text-sm" style={{ color: BRAND_PURPLE_DARK }}>{a.title}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{a.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── CCT curriculum 22-module grid ── */
function CCTCurriculumSection({ data }) {
  return (
    <div className="px-6 md:px-10 py-8 md:py-10">
      <div className="h-px mb-8" style={{ backgroundColor: `${BRAND_GOLD}40` }} />
      <h4 className="text-xl md:text-2xl font-bold text-white mb-2">
        What You Will Learn in C|CT
      </h4>
      <p className="text-sm mb-6" style={{ color: "rgba(233,213,255,0.75)" }}>
        {data.intro}
      </p>
      <div className="rounded-lg overflow-hidden" style={{ borderLeft: "4px solid rgba(252,169,3,0.5)" }}>
        <div className="grid grid-cols-1 md:grid-cols-2">
          {data.modules.map((mod, i) => (
            <div
              key={i}
              className="flex items-baseline gap-3 px-4 py-3"
              style={{
                background: Math.floor(i / 2) % 2 === 0 ? "rgba(255,255,255,0.06)" : "transparent",
              }}
            >
              <span className="font-bold flex-shrink-0 text-sm" style={{ color: BRAND_GOLD, minWidth: "26px" }}>
                {i + 1}.
              </span>
              <span className="text-sm leading-snug" style={{ color: "rgba(233,213,255,0.9)" }}>
                {mod}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── CSCU rich content: description, audience, 18-topic grid, about, outline, why ── */
function CSCURichContent({ data }) {
  const topicIcons = [
    Cloud, KeyRound, Users, Fingerprint, Mail, Globe,
    Shield, Building2, Smartphone, Database, Share2, ShieldCheck,
    RotateCcw, Lock, CreditCard, Eye, Wifi, Monitor,
  ];

  return (
    <div className="px-6 md:px-10 py-8 md:py-10">
      <div className="h-px mb-8" style={{ backgroundColor: `${BRAND_GOLD}40` }} />

      {/* 1 — Enhanced description */}
      <p className="text-base leading-relaxed mb-8" style={{ color: "rgba(233,213,255,0.9)" }}>
        {data.enhancedDesc}
      </p>

      {/* 2 — Target Audience callout box */}
      <div
        className="rounded-xl p-6 mb-10"
        style={{
          background: "rgba(216,180,254,0.08)",
          borderLeft: `4px solid ${BRAND_GOLD}`,
        }}
      >
        <h5 className="font-bold text-lg mb-3" style={{ color: BRAND_GOLD }}>
          {data.audienceHeading}
        </h5>
        <p className="text-sm leading-relaxed text-white">
          {data.audienceContent}
        </p>
      </div>

      {/* 3 — What Will You Learn? 18-topic icon grid */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-3">What Will You Learn?</h4>
      <p className="text-sm mb-6" style={{ color: "rgba(233,213,255,0.8)" }}>
        {data.learnIntro}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
        {data.topics.map((topic, i) => {
          const Icon = topicIcons[i] || Shield;
          return (
            <div
              key={i}
              className="flex flex-col items-center text-center p-3 rounded-xl"
              style={{ background: "rgba(255,255,255,0.95)", border: "1px solid rgba(252,169,3,0.2)" }}
            >
              <Icon size={28} style={{ color: BRAND_GOLD, marginBottom: "8px", flexShrink: 0 }} />
              <span className="text-xs font-bold leading-snug" style={{ color: BRAND_PURPLE_DARK }}>
                {topic}
              </span>
            </div>
          );
        })}
      </div>

      {/* 4 — About the Program */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-4">
        {data.aboutHeading}
      </h4>
      <div className="mb-10">
        {data.aboutContent.split("\n\n").map((para, i) => (
          <p key={i} className="text-sm leading-relaxed mb-4" style={{ color: "rgba(233,213,255,0.9)" }}>
            {para}
          </p>
        ))}
      </div>

      {/* 5 — Course Outline */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-3">Course Outline</h4>
      <p className="text-sm mb-5" style={{ color: "rgba(233,213,255,0.8)" }}>
        {data.outlineIntro}
      </p>
      <div className="rounded-xl overflow-hidden mb-10" style={{ background: "rgba(255,255,255,0.06)" }}>
        {data.modules.map((mod, i) => (
          <div
            key={i}
            className="flex items-center gap-4 px-5 py-3"
            style={{
              borderBottom: i < data.modules.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none",
            }}
          >
            <span
              className="text-sm font-black w-7 flex-shrink-0 text-center"
              style={{ color: BRAND_GOLD }}
            >
              {i + 1}
            </span>
            <span className="text-sm" style={{ color: "rgba(233,213,255,0.9)" }}>{mod}</span>
          </div>
        ))}
      </div>

    </div>
  );
}

/* ── CEH rich content: competitive edge, learn cards, audience, trust stats ── */
function CEHRichContent({ data }) {
  return (
    <div className="px-6 md:px-10 py-8 md:py-10">
      <div className="h-px mb-10" style={{ backgroundColor: `${BRAND_GOLD}40` }} />

      {/* 1 — Competitive Edge (white card) */}
      <div className="bg-white rounded-xl p-6 md:p-8 mb-10">
        <div className="flex flex-col md:flex-row gap-8">
          <div style={{ flex: "0 0 55%" }}>
            <h4 className="text-xl font-bold mb-1" style={{ color: BRAND_PURPLE_DARK }}>
              {data.edgeHeading}
            </h4>
            <p className="text-xs font-semibold tracking-wide uppercase mb-4" style={{ color: BRAND_GOLD }}>
              Now enhanced with AI capabilities
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mb-5">{data.edgeParagraph}</p>
            <ul className="flex flex-col gap-3">
              {[data.edgeBullet1, data.edgeBullet2, data.edgeBullet3].filter(Boolean).map((bullet, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                  <CheckIcon color={BRAND_GOLD} />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col justify-center gap-1" style={{ flex: 1 }}>
            {data.edgeStats.map((stat, i) => (
              <div
                key={i}
                className="py-3"
                style={{ borderBottom: i < data.edgeStats.length - 1 ? "1px solid #e5e7eb" : "none" }}
              >
                <span
                  className="font-black block leading-none"
                  style={{ fontSize: "clamp(1.6rem,3vw,2.2rem)", color: BRAND_GOLD }}
                >
                  {stat.pct}
                </span>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2 — What You Will Learn */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-2">
        What You Will Learn with CEH v13
      </h4>
      <p className="text-sm mb-6" style={{ color: "rgba(233,213,255,0.75)" }}>
        {data.learnIntro}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {data.learnCards.map((card, i) => (
          <div
            key={i}
            className="bg-white rounded-xl p-5 flex flex-col gap-2"
            style={{ borderTop: `4px solid ${BRAND_GOLD}` }}
          >
            <p className="font-bold text-sm" style={{ color: BRAND_PURPLE_DARK }}>{card.title}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{card.desc}</p>
          </div>
        ))}
      </div>

      {/* 3 — Who is CEH For? */}
      <h4 className="text-xl md:text-2xl font-bold text-white mb-5">Who is CEH v13 For?</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {data.audiences.map((aud, i) => (
          <div
            key={i}
            className="bg-white rounded-xl p-5 flex flex-col gap-3"
            style={{ border: "1px solid rgba(59,7,100,0.15)" }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `${BRAND_GOLD}20` }}
            >
              {i === 0 && (
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={BRAND_GOLD} strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              )}
              {i === 1 && (
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={BRAND_GOLD} strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              )}
              {i === 2 && (
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={BRAND_GOLD} strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              )}
              {i === 3 && (
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={BRAND_GOLD} strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5M6 12v5c3 3 9 3 12 0v-5" />
                </svg>
              )}
            </div>
            <p className="font-bold text-sm" style={{ color: BRAND_PURPLE_DARK }}>{aud.title}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{aud.desc}</p>
          </div>
        ))}
      </div>

      {/* 4 — Industry Trust (full-bleed dark section) */}
      <div
        className="-mx-6 md:-mx-10 px-6 md:px-10 py-8 md:py-10"
        style={{ background: "rgba(0,0,0,0.35)", borderTop: "1px solid rgba(252,169,3,0.2)" }}
      >
        <h4 className="text-xl md:text-2xl font-bold text-white mb-3">{data.trustHeading}</h4>
        <p className="text-sm mb-6" style={{ color: "rgba(233,213,255,0.75)" }}>
          {data.trustIntro}
        </p>
        <div className="flex flex-col gap-3">
          {data.trustStats.map((stat, i) => (
            <div
              key={i}
              className="flex items-center gap-4 py-2"
              style={{ borderBottom: "1px solid rgba(252,169,3,0.15)" }}
            >
              <span
                className="font-black text-lg px-3 py-1.5 flex-shrink-0"
                style={{
                  background: "rgba(0,0,0,0.4)",
                  color: BRAND_GOLD,
                  minWidth: "72px",
                  textAlign: "center",
                  borderRadius: "6px",
                }}
              >
                {stat.pct}
              </span>
              <p className="text-sm text-white leading-relaxed">{stat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Brochure-style row for each certification ── */
function OfferingRow({ abbr, version, fullName, duration, mode, fee, description, divider }) {
  return (
    <div>
      {divider && (
        <div
          className="h-px mx-6 md:mx-10"
          style={{ backgroundColor: `${BRAND_GOLD}40` }}
        />
      )}
      <div className="flex flex-col md:grid md:grid-cols-[35%_1fr]">

        {/* LEFT — abbreviation, version, name, duration */}
        <div
          className="flex flex-col justify-center px-6 md:px-10 py-8 md:py-10 relative"
          style={{ borderRight: `1px solid ${BRAND_GOLD}40` }}
        >
          {/* gold vertical accent line visible only on desktop */}
          <div className="flex items-end gap-3 flex-wrap">
            <span
              className="font-black leading-none"
              style={{ fontSize: "clamp(3rem,7vw,4rem)", color: BRAND_GOLD }}
            >
              {abbr}
            </span>
            {version && (
              <span
                className="font-bold text-xl mb-1"
                style={{ color: BRAND_GOLD }}
              >
                {version}
              </span>
            )}
          </div>
          <p
            className="font-bold tracking-widest mt-2 text-sm uppercase"
            style={{ color: "rgba(255,255,255,0.9)" }}
          >
            {fullName}
          </p>
          <p
            className="mt-3 text-sm font-bold tracking-wide"
            style={{ color: BRAND_GOLD }}
          >
            DURATION:&nbsp;{duration}
          </p>
          {mode && (
            <p
              className="mt-1 text-sm font-bold tracking-wide"
              style={{ color: BRAND_GOLD }}
            >
              MODE:&nbsp;{mode}
            </p>
          )}
          {fee && (
            <p
              className="mt-1 text-sm font-bold tracking-wide"
              style={{ color: BRAND_GOLD }}
            >
              FEE:&nbsp;{fee}
            </p>
          )}
        </div>

        {/* RIGHT — description */}
        <div className="flex items-center px-6 md:px-10 py-8 md:py-10">
          <p
            className="text-base leading-relaxed"
            style={{ color: "rgba(233,213,255,0.9)" }}
          >
            {description}
          </p>
        </div>

      </div>
    </div>
  );
}

const ECCouncilDetailPage = () => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeSection = useActiveSection();

  useEffect(() => {
    cmsApi
      .getProgramBySlug("ec-council-certification")
      .then((res) => {
        if (res.success && res.program) setProgram(res.program);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const benefitsSource = program?.benefits ?? program?.careerOpportunities ?? null;
  const benefits       = benefitsSource ? parseBenefits(benefitsSource) : [];
  const admissionText  = program?.admissionRequirements ?? program?.requirements ?? "";

  /* ── offerings data: fall back to hard-coded defaults ── */
  const offeringsIntro =
    program?.eccouncilOfferingsIntro ||
    "At JRIIT, we proudly offer three EC-Council certification programs — each designed to build real, practical cybersecurity skills recognised by employers worldwide.";

  const cehBundles = {
    heading:  program?.eccouncilCehBundleHeading  || "Choose Your CEH Program",
    subtitle: program?.eccouncilCehBundleSubtitle || "We offer two CEH bundle options to match your learning goals and budget.",
    bundle1: {
      name:     program?.eccouncilBundle1Name    || "C|EH AI Elite",
      tagline:  program?.eccouncilBundle1Tagline || "Learn, Certify, Engage & Compete",
      features: program?.eccouncilBundle1Features ||
        "CEH v13 AI Courseware\nCEH Engage (Practical Exam)\nCEH Compete (CTF Challenges)\niLabs – 6 months access\nEC-Council Official Exam Voucher\nCertification upon passing\nCareer path guidance\nJRIIT mentorship sessions",
      color:   program?.eccouncilBundle1Color   || '#fe0201',
      popular: program?.eccouncilBundle1Popular !== undefined ? program.eccouncilBundle1Popular : true,
    },
    bundle2: {
      name:     program?.eccouncilBundle2Name    || "C|EH AI",
      tagline:  program?.eccouncilBundle2Tagline || "Learn & Certify",
      included: program?.eccouncilBundle2Included ||
        "CEH v13 AI Courseware\nEC-Council Official Exam Voucher\nCertification upon passing",
      excluded: program?.eccouncilBundle2Excluded ||
        "CEH Engage (Practical Exam)\nCEH Compete (CTF Challenges)\niLabs – 6 months access\nCareer path guidance\nJRIIT mentorship sessions",
      color:   program?.eccouncilBundle2Color   || '#ffa243',
    },
  };

  const cctRich = {
    enhancedDesc: program?.eccouncilCctEnhancedDesc ||
      "Kickstart your career in cybersecurity with the C|CT — EC-Council's entry-level certification designed for individuals who want real, practical skills from day one. The C|CT goes beyond teaching fundamental concepts by validating your IT and cybersecurity skills through extensive hands-on practice using a Live Cyber Range with real attack systems and live targets. Establishing this strong technical foundation lays the groundwork for a future career across many cybersecurity domains including ethical hacking, penetration testing, digital forensics, and application security. At JRIIT, we deliver C|CT training as an authorised EC-Council partner in Tanzania — giving you world-class cybersecurity education right here at home.",
    talentHeadline: program?.eccouncilCctTalentHeadline || "The Cybersecurity Skills Gap Is Real",
    talentBody: program?.eccouncilCctTalentBody ||
      "82% of employers report a shortage of cybersecurity talent. The industry urgently needs skilled professionals who can tackle the ever-growing global threat of cybercrime. Cybersecurity analysts earn between USD 54,000 and USD 116,000 per year — yet qualified candidates remain scarce. The C|CT was built specifically to bridge this gap, equipping you with the practical, validated skills employers are desperately looking for.",
    stats: [
      {
        value: program?.eccouncilCctStat1Value || "85",
        label: program?.eccouncilCctStat1Label || "Real-life Practical Hands-on Labs",
        text:  program?.eccouncilCctStat1Text  || "The only baseline cybersecurity program worldwide offering this many real-world labs.",
      },
      {
        value: program?.eccouncilCctStat2Value || "LIVE",
        label: program?.eccouncilCctStat2Label || "Cyber Range Experience",
        text:  program?.eccouncilCctStat2Text  || "An immersive practical certification delivered on a live Cyber Range — not simulations.",
      },
      {
        value: program?.eccouncilCctStat3Value || "50%",
        label: program?.eccouncilCctStat3Label || "Training Time Dedicated to Labs",
        text:  program?.eccouncilCctStat3Text  || "Half of your training time is spent doing — not just listening or reading.",
      },
      {
        value: program?.eccouncilCctStat4Value || "CTF",
        label: program?.eccouncilCctStat4Label || "Capture the Flag Challenges",
        text:  program?.eccouncilCctStat4Text  || "Performance-based exam combined with Live Cyber Range activities and CTF-style critical thinking challenges.",
      },
      {
        value: program?.eccouncilCctStat5Value || "4",
        label: program?.eccouncilCctStat5Label || "Disciplines Covered",
        text:  program?.eccouncilCctStat5Text  || "Multidisciplinary learning across Network Defense, Ethical Hacking, Digital Forensics, and Security Operations.",
      },
    ],
    offerings: [
      {
        title: program?.eccouncilCctOffering1Title || "Strong Foundational Coverage",
        desc:  program?.eccouncilCctOffering1Desc  || "The C|CT provides total foundational cybersecurity domain coverage with key concepts in each domain, combined with practical hands-on labs and critical thinking challenges — producing world-class cybersecurity technologists.",
      },
      {
        title: program?.eccouncilCctOffering2Title || "Live Range Experience",
        desc:  program?.eccouncilCctOffering2Desc  || "Unlike other programs that rely on simulations, C|CT is delivered on a live Cyber Range utilizing live targets and real attack systems for a truly immersive, real-life practice and assessment platform.",
      },
      {
        title: program?.eccouncilCctOffering3Title || "Capture the Flag",
        desc:  program?.eccouncilCctOffering3Desc  || "C|CT offers CTF-style critical thinking challenges accompanying each lab exercise — putting knowledge into practice and providing a proven record of skill demonstration that employers can verify.",
      },
      {
        title: program?.eccouncilCctOffering4Title || "Multiple Certifications Pathway",
        desc:  program?.eccouncilCctOffering4Desc  || "The C|CT program goes above and beyond common entry-level programs. Candidates who attain C|CT will be well prepared to earn other leading cybersecurity certifications including Security+ without further training.",
      },
      {
        title: program?.eccouncilCctOffering5Title || "Most Affordable",
        desc:  program?.eccouncilCctOffering5Desc  || "Despite its unique heavily hands-on design and real-world cyber range capability, C|CT is one of the most affordable cybersecurity certifications in the world — making it accessible for every serious student.",
      },
    ],
    jobRoles: (
      program?.eccouncilCctJobRoles ||
      "IT Networking Specialist\nCybersecurity Technician\nNetwork Administrator\nNetwork Engineer\nIT Manager\nSecurity Operations Center (SOC) Analyst"
    ).split("\n").filter(Boolean),
    enrollIntro: program?.eccouncilCctEnrollIntro ||
      "The C|CT is ideal for anyone looking to start their career in cybersecurity or build a strong foundational understanding of the concepts and techniques required to be effective on the job.",
    audience1Title: program?.eccouncilCctAudience1Title || "Early-Career Professionals",
    audience1Desc:  program?.eccouncilCctAudience1Desc  ||
      "Early-career IT professionals, IT managers, career changers, and career advancers looking to validate their cybersecurity skills and open doors to specialized roles.",
    audience2Title: program?.eccouncilCctAudience2Title || "Students & Recent Graduates",
    audience2Desc:  program?.eccouncilCctAudience2Desc  ||
      "Students and recent graduates who want to enter the cybersecurity workforce with a globally recognised, hands-on certification that employers trust and value.",
  };

  const cctCurriculum = {
    intro:
      program?.eccouncilCctCurriculumIntro ||
      "The C|CT curriculum covers 22 comprehensive modules spanning all core cybersecurity domains — giving you a well-rounded, job-ready foundation from day one.",
    modules: (
      program?.eccouncilCctCurriculumModules ||
      "Information Security Threats and Vulnerabilities\nInformation Security Attacks\nNetwork Security Fundamentals\nIdentification, Authentication, and Authorization\nNetwork Security Controls — Administrative Controls\nNetwork Security Controls — Physical Controls\nNetwork Security Controls — Technical Controls\nNetwork Security Assessment Techniques and Tools\nApplication Security\nVirtualization and Cloud Computing\nWireless Network Security\nMobile Device Security\nInternet of Things (IoT) and Operational Technology (OT) Security\nCryptography\nData Security\nNetwork Troubleshooting\nNetwork Traffic Monitoring\nNetwork Log Monitoring and Analysis\nIncident Response\nComputer Forensics\nBusiness Continuity and Disaster Recovery\nRisk Management"
    ).split("\n").filter(Boolean),
  };

  const cehRich = {
    edgeHeading:   program?.eccouncilCehEdgeHeading   || "Gain a Competitive Edge with CEH v13",
    edgeParagraph: program?.eccouncilCehEdgeParagraph ||
      "CEH v13 — the latest evolution of the world's most recognised ethical hacking certification — is now powered by AI capabilities. Structured across 20 learning modules and covering more than 550 attack techniques, CEH v13 gives you the core knowledge and hands-on skills needed to succeed as a cybersecurity professional in today's AI-driven threat landscape.",
    edgeBullet1: program?.eccouncilCehEdgeBullet1 || "Flexible learning options to match your schedule and goals",
    edgeBullet2: program?.eccouncilCehEdgeBullet2 || "A globally recognised certification trusted by employers worldwide",
    edgeBullet3: program?.eccouncilCehEdgeBullet3 || "Practical learning with 221 hands-on labs in a real cyber range",
    edgeStats: [
      { pct: program?.eccouncilCehStat1Pct || "92%",    desc: program?.eccouncilCehStat1Desc || "of employers prefer CEH graduates for ethical hacking positions" },
      { pct: program?.eccouncilCehStat2Pct || "95%",    desc: program?.eccouncilCehStat2Desc || "of CEH holders chose it specifically for career advancement" },
      { pct: program?.eccouncilCehStat3Pct || "45+",    desc: program?.eccouncilCehStat3Desc || "cybersecurity job roles that CEH modules are mapped to" },
      { pct: program?.eccouncilCehStat4Pct || "4 in 5", desc: program?.eccouncilCehStat4Desc || "companies now consider AI integration a strategic security priority" },
      { pct: program?.eccouncilCehStat5Pct || "1 in 2", desc: program?.eccouncilCehStat5Desc || "CEH-certified professionals received a promotion after certification" },
    ],
    learnIntro: program?.eccouncilCehLearnIntro ||
      "CEH v13 gives you the skills and knowledge to drive your cybersecurity career forward in the age of AI. You will learn to think and act like an ethical hacker — understanding how attackers operate so you can defend systems before they strike.",
    learnCards: [
      { title: program?.eccouncilCehLearnCard1Title || "Find and Fix Weaknesses",       desc: program?.eccouncilCehLearnCard1Desc || "Discover how attackers exploit systems and learn the ethical hacking methodologies needed to identify vulnerabilities and strengthen your organisation's defenses." },
      { title: program?.eccouncilCehLearnCard2Title || "Become a Security Expert",       desc: program?.eccouncilCehLearnCard2Desc || "Master the tools and techniques used by top cybersecurity professionals to assess, test, and reinforce the security posture of any organisation." },
      { title: program?.eccouncilCehLearnCard3Title || "Protect Reputations and Data",   desc: program?.eccouncilCehLearnCard3Desc || "Learn to proactively prevent data breaches, respond to security incidents, and safeguard the trust of customers and stakeholders." },
      { title: program?.eccouncilCehLearnCard4Title || "Master Ethical Hacking with AI", desc: program?.eccouncilCehLearnCard4Desc || "Leverage AI-driven techniques to enhance your ethical hacking skills, automate threat detection, and stay ahead of today's most sophisticated cyber threats." },
    ],
    audiences: [
      { title: program?.eccouncilCehAud1Title || "Cybersecurity Professionals", desc: program?.eccouncilCehAud1Desc || "Advance your cybersecurity career with the world's most recognised ethical hacking certification, now enhanced with the power of AI." },
      { title: program?.eccouncilCehAud2Title || "Teams and Organizations",     desc: program?.eccouncilCehAud2Desc || "Equip your security team with certified ethical hacking skills to proactively identify and neutralize threats before they impact your business." },
      { title: program?.eccouncilCehAud3Title || "Government and Military",     desc: program?.eccouncilCehAud3Desc || "CEH is trusted and highly valued by government agencies, defense bodies, and intelligence communities across more than 140 countries worldwide." },
      { title: program?.eccouncilCehAud4Title || "Educators and Institutions",  desc: program?.eccouncilCehAud4Desc || "Deliver world-class cybersecurity education to your students with EC-Council's industry-leading certification programs and updated curriculum." },
    ],
    trustHeading: program?.eccouncilCehTrustHeading || "Why Organizations Worldwide Trust CEH",
    trustIntro:   program?.eccouncilCehTrustIntro   ||
      "For more than 25 years, EC-Council's cybersecurity programs have equipped professionals across the globe with the knowledge and skills to combat cyberattacks. Here is what CEH-certified professionals report about their experience:",
    trustStats: [
      { pct: program?.eccouncilCehTrust1Pct || "100%", desc: program?.eccouncilCehTrust1Desc || "say CEH significantly increased their professional recognition and respect in the workplace" },
      { pct: program?.eccouncilCehTrust2Pct || "99%",  desc: program?.eccouncilCehTrust2Desc || "report that their investment in CEH directly benefited their career and professional growth" },
      { pct: program?.eccouncilCehTrust3Pct || "99%",  desc: program?.eccouncilCehTrust3Desc || "found the hands-on CEH labs essential for developing real-world ethical hacking skills" },
      { pct: program?.eccouncilCehTrust4Pct || "98%",  desc: program?.eccouncilCehTrust4Desc || "credit CEH as a decisive factor in their transition into a cybersecurity career" },
      { pct: program?.eccouncilCehTrust5Pct || "97%",  desc: program?.eccouncilCehTrust5Desc || "say CEH effectively prepared them for emerging cyber threats and current industry trends" },
    ],
  };

  const cscuRich = {
    enhancedDesc: program?.eccouncilCscuEnhancedDesc ||
      "The CSCU training program equips students with the knowledge and skills needed to protect their personal and organisational information assets. The program is designed to interactively teach students about the full range of information security threats they face in everyday digital life — from identity theft and credit card fraud to phishing scams, malware, social engineering, and loss of sensitive data. The skills gained through this program will not only help students identify these threats but also take practical steps to mitigate them effectively. This certification is an excellent starting point for anyone who uses computers and the internet — and a powerful complement to any educational background in security and networking.",
    audienceHeading: program?.eccouncilCscuAudienceHeading || "Who Is This Course For?",
    audienceContent: program?.eccouncilCscuAudienceContent ||
      "This course is specifically designed for today's computer users who use the internet and the web extensively to work, study, and play. Any individual who uses computers, surfs social media, sends emails, or chats online — but is not fully aware of computer security risks — will benefit greatly from this program. No technical background is required.",
    learnIntro: program?.eccouncilCscuLearnIntro ||
      "CSCU covers 18 practical security topics that every computer user needs to understand in today's connected world.",
    topics: [
      program?.eccouncilCscuTopic1  || "Cloud Security",
      program?.eccouncilCscuTopic2  || "Password Security",
      program?.eccouncilCscuTopic3  || "Social Engineering Countermeasures",
      program?.eccouncilCscuTopic4  || "Mitigating Identity Theft",
      program?.eccouncilCscuTopic5  || "Email Security",
      program?.eccouncilCscuTopic6  || "Safe Browsing",
      program?.eccouncilCscuTopic7  || "Data Protection",
      program?.eccouncilCscuTopic8  || "Physical Security",
      program?.eccouncilCscuTopic9  || "Mobile Device Security",
      program?.eccouncilCscuTopic10 || "Data Backup",
      program?.eccouncilCscuTopic11 || "Social Network Security",
      program?.eccouncilCscuTopic12 || "Antiviruses Protection",
      program?.eccouncilCscuTopic13 || "Disaster Recovery",
      program?.eccouncilCscuTopic14 || "Internet Security",
      program?.eccouncilCscuTopic15 || "Credit Card Security",
      program?.eccouncilCscuTopic16 || "Monitoring Kids Online",
      program?.eccouncilCscuTopic17 || "Wireless & Home Network Security",
      program?.eccouncilCscuTopic18 || "OS Security",
    ],
    aboutHeading: program?.eccouncilCscuAboutHeading || "About the C|SCU Program",
    aboutContent: program?.eccouncilCscuAboutContent ||
      "The CSCU curriculum is designed to educate everyday computer users on the practical aspects of networking and security — empowering them to expand their digital skills and protect themselves online. Students develop a foundational understanding of a wide range of computer and network security concerns including identity theft, credit card fraud, online banking phishing scams, malware, loss of sensitive information, and social engineering attacks.\n\nAt JRIIT, educational institutions provide students not only with one of the most current cybersecurity programs available today, but with a globally recognised certification that empowers them in both the corporate world and their personal digital lives. The courseware includes scenario-based discussions to ensure students gain real, applicable skills they can use immediately.",
    outlineIntro: program?.eccouncilCscuOutlineIntro ||
      "The CSCU program covers 10 focused modules designed to give every student a complete foundation in personal and professional cybersecurity.",
    modules: [
      program?.eccouncilCscuModule1  || "Introduction to Security",
      program?.eccouncilCscuModule2  || "Securing Operating Systems",
      program?.eccouncilCscuModule3  || "Malware and Antivirus",
      program?.eccouncilCscuModule4  || "Internet Security",
      program?.eccouncilCscuModule5  || "Security on Social Networking Sites",
      program?.eccouncilCscuModule6  || "Securing Email Communications",
      program?.eccouncilCscuModule7  || "Securing Mobile Devices",
      program?.eccouncilCscuModule8  || "Securing the Cloud",
      program?.eccouncilCscuModule9  || "Securing Network Connections",
      program?.eccouncilCscuModule10 || "Data Backup and Disaster Recovery",
    ],
  };

  // Mode/Fee per cert are repurposed into the ciscoAdditionalSection field
  // (legacy/unused Cisco field, unrelated to EC-Council — just a spare JSON slot).
  let ecModeFee = {};
  try {
    if (program?.ciscoAdditionalSection) {
      const parsed = JSON.parse(program.ciscoAdditionalSection);
      if (parsed && typeof parsed === "object") ecModeFee = parsed;
    }
  } catch {}

  const offerings = [
    {
      id:          "ceh",
      abbr:        program?.eccouncilCehAbbr     || "CEH",
      version:     program?.eccouncilCehVersion  || "v13",
      fullName:    program?.eccouncilCehFullname || "CERTIFIED ETHICAL HACKER",
      duration:    program?.eccouncilCehDuration || "1 Month 2 Weeks",
      mode:        ecModeFee.ceh?.mode || "",
      fee:         ecModeFee.ceh?.fee || "",
      description: program?.eccouncilCehDesc     ||
        "Master network security as you learn to think and act like a hacker. This course equips you with ethical hacking methodologies to identify vulnerabilities and strengthen the security of systems and networks. CEH v13 is EC-Council's flagship certification and is recognised by governments, military organisations, and top corporations worldwide as the gold standard for ethical hacking professionals.",
    },
    {
      id:          "cct",
      abbr:        program?.eccouncilCctAbbr     || "CCT",
      version:     program?.eccouncilCctVersion  || "",
      fullName:    program?.eccouncilCctFullname || "CERTIFIED CYBER SECURITY TECHNICIAN",
      duration:    program?.eccouncilCctDuration || "1 Month 2 Weeks",
      mode:        ecModeFee.cct?.mode || "",
      fee:         ecModeFee.cct?.fee || "",
      description: program?.eccouncilCctDesc     ||
        "Develop essential skills to manage and mitigate cyber threats effectively. This course offers practical experience in securing networks and handling security incidents across various domains. CCT provides a strong foundation in cybersecurity operations — covering threat identification, network defense, digital forensics basics, and incident response — preparing you for real-world security roles from day one.",
    },
    {
      id:          "cscu",
      abbr:        program?.eccouncilCscuAbbr     || "CSCU",
      version:     program?.eccouncilCscuVersion  || "",
      fullName:    program?.eccouncilCscuFullname || "CERTIFIED SECURE COMPUTER USER",
      duration:    program?.eccouncilCscuDuration || "3 Weeks",
      mode:        ecModeFee.cscu?.mode || "",
      fee:         ecModeFee.cscu?.fee || "",
      description: program?.eccouncilCscuDesc     ||
        "Learn crucial techniques to safeguard your personal and organisational data. This course targets everyday computer users, enhancing their defenses against online threats, phishing attacks, identity theft, and data loss. CSCU is the ideal starting point for anyone wanting to build a strong foundation in personal cybersecurity awareness and safe digital practices before advancing to more technical certifications.",
    },
  ];

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">
        <CourseHeader
          title="EC-Council"
          highlight="Certification Programs"
          subtitle="The world's leading ethical hacking and cybersecurity certifications — now in Tanzania."
          backgroundImage={program?.heroImage ? getImageUrl(program.heroImage) : cyberImg}
          cta={{ label: "Start Application", targetId: "application-form" }}
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Professional Courses", href: "/course/professional-courses" },
            { label: "EC-Council" },
          ]}
        />

        {loading ? (
          <div className="flex justify-center items-center py-32">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-custom-pink" />
          </div>
        ) : (
          <div className="max-w-[1500px] mx-auto px-4 sm:px-6">
            <div className="flex items-start gap-8">

              {/* Sticky Quick Navigation sidebar */}
              <aside className="hidden lg:block w-64 xl:w-72 shrink-0 sticky top-20 self-start h-fit pt-10 pl-2 xl:pl-4">
                <QuickNavigation activeSection={activeSection} />
              </aside>

              {/* Main content column */}
              <div className="flex-1 min-w-0">

                {/* 1 — Certifications */}
                <div id="certification-programs">
                  <AnimatedSection title="EC-Council Certification Programs">
                    <p className="text-base md:text-lg lg:text-xl text-gray-800 leading-loose mb-10">
                      {program?.description}
                    </p>

                    <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8">
                      {offeringsIntro}
                    </p>

                    {/* Dark purple brochure panel */}
                    <div
                      className="rounded-2xl overflow-hidden"
                      style={{
                        background: `linear-gradient(135deg, ${BRAND_PURPLE_DARK} 0%, ${BRAND_PURPLE_MID} 100%)`,
                      }}
                    >
                      {offerings.map((cert, i) => (
                        <React.Fragment key={cert.id}>
                          <OfferingRow
                            abbr={cert.abbr}
                            version={cert.version}
                            fullName={cert.fullName}
                            duration={cert.duration}
                            mode={cert.mode}
                            fee={cert.fee}
                            description={cert.description}
                            divider={i > 0}
                          />
                          {cert.id === "ceh" && (
                            <CEHBundleComparison
                              heading={cehBundles.heading}
                              subtitle={cehBundles.subtitle}
                              bundle1={cehBundles.bundle1}
                              bundle2={cehBundles.bundle2}
                            />
                          )}
                          {cert.id === "ceh" && <CEHRichContent data={cehRich} />}
                          {cert.id === "cct" && <CCTRichContent data={cctRich} />}
                          {cert.id === "cct" && <CCTCurriculumSection data={cctCurriculum} />}
                          {cert.id === "cscu" && <CSCURichContent data={cscuRich} />}
                        </React.Fragment>
                      ))}
                    </div>
                  </AnimatedSection>
                </div>

                {/* 2 — Program Benefits */}
                <div id="program-benefits">
                  <ProgramBenefits programName="EC-Council" benefits={benefits} />
                </div>

                {/* 3 — Admission Requirements */}
                <div id="admission-requirements">
                  <AnimatedSection title="ADMISSION REQUIREMENTS">
                    <p className="text-base md:text-lg lg:text-xl text-gray-800 leading-loose tracking-wide">
                      {admissionText}
                    </p>
                  </AnimatedSection>
                </div>

                {/* 4 — Application Form */}
                <ProfessionalCourseApplicationForm
                  programName="EC-Council"
                  fieldLabel="Professional Course"
                />

              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default ECCouncilDetailPage;
